import React from 'react'
import styled from '@emotion/styled'
import { Button, Flex, Icon, Text } from '@mindfulchefuk/design-system'
import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import { quantityAndPortionCopy } from '@mindfulchefuk/utils/quantityAndPortionCopy'

export type TAddSubtractQuantityButtonGroup = {
  addButtonTestId?: string
  isAdditionDisabled?: boolean
  itemName?: string
  isModal: boolean
  quantity: number
  quantityTestId?: string
  onAdd: () => void
  onSubtract: () => void
  subtractButtonTestId?: string
  portionCount?: PortionCount
  showPortionAndQuantity: boolean
}

type TQuantityButton = {
  buttonHeightAndWidth: string
}

const StyledButton = styled(Button)<TQuantityButton>`
  width: ${(props) => props.buttonHeightAndWidth}};
  height: ${(props) => props.buttonHeightAndWidth}};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 10px;
`

const StyledIcon = styled(Icon)`
  margin-top: 3px;
`

const StyledText = styled(Text)`
  line-height: 34px;
`

export const AddSubtractQuantityButtonGroup = ({
  addButtonTestId = 'quantity-selector-add-button',
  isAdditionDisabled = false,
  isModal,
  itemName = 'item',
  quantity,
  quantityTestId = 'quantity-selector-item-quantity',
  onAdd,
  onSubtract,
  subtractButtonTestId = 'quantity-selector-subtract-button',
  portionCount,
  showPortionAndQuantity,
}: TAddSubtractQuantityButtonGroup) => {
  const verb = quantity > 1 ? 'are' : 'is'
  const quantityLabel = quantityAndPortionCopy({
    showPortionAndQuantity,
    quantity,
    portionCount,
  })

  // all the styling dependent on isModal
  const buttonHeightAndWidth = isModal ? '45px' : '34px'
  const buttonCopySize = isModal ? 'body-md-500-caps' : 'body-xl-500'
  const dataTestId = isModal
    ? 'modal-quantity-button-group'
    : 'quantity-button-group'

  return (
    <Flex
      height={buttonHeightAndWidth}
      alignItems="center"
      data-testid={dataTestId}
    >
      <StyledButton
        aria-label={`Remove ${itemName} from basket`}
        data-testid={subtractButtonTestId}
        onClick={(event) => {
          event.stopPropagation()
          onSubtract()
        }}
        mr={12}
        size="small"
        variant="primary-square"
        buttonHeightAndWidth={buttonHeightAndWidth}
      >
        <StyledIcon size={20} type="minus" color="white" />
      </StyledButton>
      <StyledText
        aria-label={`There ${verb} ${quantityLabel} of ${itemName} in your basket`}
        color="black"
        data-testid={quantityTestId}
        textAlign="center"
        variant={buttonCopySize}
      >
        {quantityLabel}
      </StyledText>
      <StyledButton
        aria-label={`Add ${itemName} to basket`}
        data-testid={addButtonTestId}
        disabled={isAdditionDisabled}
        onClick={(event) => {
          event.stopPropagation()
          onAdd()
        }}
        ml={12}
        size="small"
        variant="primary-square"
        buttonHeightAndWidth={buttonHeightAndWidth}
      >
        <StyledIcon type="plus" color="white" />
      </StyledButton>
    </Flex>
  )
}
